import { useEffect, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { OptImg } from "@/components/marketing/OptImg";

/**
 * New-UI landing page. Ports Foreman Landing.html structure to React.
 * All static markup; existing /login, /onboard, /articles routes wired
 * via React Router. Smooth-scroll on hash anchors, scroll-state nav.
 */
// Problem-scene task bubbles: the manual grind, one per desk.
// `base` is the count-up timer's starting point in seconds; `secs`
// is the tick chip that pops back at the desk once Foreman has
// swallowed the task — 11 minutes and counting versus seconds.
// Three desk tasks (desktop shows all three, one per logo row). Mobile
// shows the first two (repositioned left+right) and jumps the fill from
// the bottom row straight to full — see the 880 block in foreman-ui.css.
const OFFICE_TASKS = [
  // `hand`/`fm` are the realistic, human-readable times used by the
  // mobile flat-story (delegation time-saving); `base`/`secs` drive the
  // desktop animation's counting clock + done chip.
  { label: "Reviewing 200 completed jobs", fill: 34, base: 692, secs: "4s", hand: "3 hours", fm: "5 min" },
  { label: "Raising 30 invoices", fill: 61, base: 845, secs: "2s", hand: "2 hours", fm: "3 min" },
  { label: "Retyping 45 job sheets", fill: 48, base: 588, secs: "6s", hand: "90 minutes", fm: "2 min" },
];
const fmtTimer = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function LandingPageNew() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  // Live count-up for the Problem-scene task timers.
  const [officeTick, setOfficeTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setOfficeTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Hero orbit Tasks card — rows are toggleable toys. `entrance` holds
  // the staggered tick-draw delay class for the initial reveal; it's
  // dropped the first time a row is toggled so re-ticks are instant.
  const [heroTasks, setHeroTasks] = useState([
    { done: true, entrance: "d1" },
    { done: true, entrance: "d2" },
    { done: false, entrance: "" },
  ]);
  const toggleHeroTask = (i: number) =>
    setHeroTasks((rows) =>
      rows.map((r, j) => (j === i ? { done: !r.done, entrance: "" } : r)),
    );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Pinned scroll story for the Problem scene. The section is a
  // 340vh track with a sticky full-viewport stage; pin progress
  // q (0→1) drives the beats: reveal on approach, then each desk's
  // bubble pours into the glass Foreman logo (pour1/2/3 — the CSS
  // --fill steps the liquid in thirds), then tick chips return to
  // the desks and the copy swaps (.resolved) and HOLDS through the
  // rest of the pin so the payoff line gets read. CSS staging lives
  // under `.scene-problem` in foreman-ui.css; the preview HTML
  // mirrors this with an inline script — keep the two in sync.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(".scene-problem");
    if (!sec) return;
    // Mobile shows a static story block (.flat-story); the pinned stage
    // is hidden by CSS, so skip the scroll handler entirely (no pin, no
    // animation, no .done collapse). Desktop keeps the pinned story.
    if (window.matchMedia("(max-width: 640px)").matches) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    // One-way story: once fully scrolled through, freeze the final
    // frame and collapse the scroll track — scrolling back up must
    // not rewind the animation. scrollBy keeps the viewport steady
    // through the height change.
    let done = false;
    let raf = 0;
    const onSceneScroll = () => {
      if (reduced || done || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (done) return;
        const r = sec.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom <= vh + 1) {
          done = true;
          // The collapse shrinks the page's max-scroll, so the
          // browser clamps the offset by itself — a relative
          // scrollBy would double-correct straight past the top of
          // the page. Absolute target computed BEFORE the collapse;
          // "instant" so the site's smooth scroll-behavior can't
          // animate the correction.
          const yBefore = window.scrollY;
          const prevH = sec.offsetHeight;
          sec.classList.add(
            "in-view",
            "pour1",
            "pour2",
            "pour3",
            "resolved",
            "done",
          );
          const delta = prevH - sec.offsetHeight;
          if (delta > 0) {
            window.scrollTo({
              top: Math.max(0, yBefore - delta),
              left: 0,
              behavior: "instant",
            });
          }
          return;
        }
        const track = Math.max(1, r.height - vh);
        const q = Math.min(1, Math.max(0, -r.top / track));
        sec.classList.toggle("in-view", r.top < vh * 0.5);
        // Three pours (desktop fills row by row); mobile shows 2 cards
        // and the CSS jumps its fill bottom-row→full. A long post-payoff
        // dwell holds the payoff line before the pin releases.
        sec.classList.toggle("pour1", q > 0.24);
        sec.classList.toggle("pour2", q > 0.37);
        sec.classList.toggle("pour3", q > 0.5);
        sec.classList.toggle("resolved", q > 0.67);
      });
    };
    window.addEventListener("scroll", onSceneScroll, { passive: true });
    onSceneScroll();
    return () => {
      window.removeEventListener("scroll", onSceneScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Hub scene: pinned scroll story, the same grammar as the Problem
  // handler — q drives the beats (command, then each system glowing
  // in work order, then the closing line), with the same one-way
  // .done latch collapsing the track once fully scrolled through.
  // Glow windows are static clip-path %s inside .hub-frame, so no
  // measuring here. Keep the preview inline script in sync.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(".scene-hub");
    if (!sec) return;
    // Mobile shows a static story block (.flat-story) — skip the handler.
    if (window.matchMedia("(max-width: 640px)").matches) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let done = false;
    let raf = 0;
    const onHubScroll = () => {
      if (reduced || done || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (done) return;
        const r = sec.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom <= vh + 1) {
          done = true;
          const yBefore = window.scrollY;
          const prevH = sec.offsetHeight;
          sec.classList.add(
            "in-view",
            "cmd",
            "lit1",
            "lit2",
            "lit3",
            "fin",
            "done",
          );
          const delta = prevH - sec.offsetHeight;
          if (delta > 0) {
            window.scrollTo({
              top: Math.max(0, yBefore - delta),
              left: 0,
              behavior: "instant",
            });
          }
          return;
        }
        const track = Math.max(1, r.height - vh);
        const q = Math.min(1, Math.max(0, -r.top / track));
        sec.classList.toggle("in-view", r.top < vh * 0.5);
        sec.classList.toggle("cmd", q > 0.16);
        sec.classList.toggle("lit1", q > 0.34);
        sec.classList.toggle("lit2", q > 0.5);
        sec.classList.toggle("lit3", q > 0.66);
        sec.classList.toggle("fin", q > 0.8);
      });
    };
    window.addEventListener("scroll", onHubScroll, { passive: true });
    onHubScroll();
    return () => {
      window.removeEventListener("scroll", onHubScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Security "He can / He never": one-way staggered reveal.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(".security-glass");
    if (!sec) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          sec.classList.add("in-view");
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(sec);
    return () => io.disconnect();
  }, []);

  // Prove-himself scroll story: pinned stage, q steps the beats,
  // one-way .done latch identical to the Problem/Hub handlers.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(".plugin");
    if (!sec) return;
    // Mobile shows a static story block (.flat-story) — skip the handler.
    if (window.matchMedia("(max-width: 640px)").matches) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let done = false;
    let raf = 0;
    const onProveScroll = () => {
      if (reduced || done || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (done) return;
        const r = sec.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.bottom <= vh + 1) {
          done = true;
          const yBefore = window.scrollY;
          const prevH = sec.offsetHeight;
          sec.classList.add("in-view", "b2", "b3", "b4", "done");
          const delta = prevH - sec.offsetHeight;
          if (delta > 0) {
            window.scrollTo({
              top: Math.max(0, yBefore - delta),
              left: 0,
              behavior: "instant",
            });
          }
          return;
        }
        const track = Math.max(1, r.height - vh);
        const q = Math.min(1, Math.max(0, -r.top / track));
        sec.classList.toggle("in-view", r.top < vh * 0.5);
        sec.classList.toggle("b2", q > 0.26);
        sec.classList.toggle("b3", q > 0.5);
        sec.classList.toggle("b4", q > 0.74);
      });
    };
    window.addEventListener("scroll", onProveScroll, { passive: true });
    onProveScroll();
    return () => {
      window.removeEventListener("scroll", onProveScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Final CTA: one-way reveal.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(".final-glass");
    if (!sec) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          sec.classList.add("in-view");
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(sec);
    return () => io.disconnect();
  }, []);
  // Pricing offer letter: one-way reveal.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(".pricing-offer");
    if (!sec) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          sec.classList.add("in-view");
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(sec);
    return () => io.disconnect();
  }, []);
  // Capabilities examples: each badge/bubble/fact reveals once as
  // it scrolls into view — no pin.
  useEffect(() => {
    const pairs = Array.from(
      document.querySelectorAll<HTMLElement>(".caps-glass .ask-ex"),
    );
    if (!pairs.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("seen");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.45 },
    );
    pairs.forEach((p) => io.observe(p));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    if (!el) return;
    const jump = () =>
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY,
        behavior: "instant" as ScrollBehavior,
      });
    requestAnimationFrame(() => {
      jump();
      requestAnimationFrame(jump);
      setTimeout(jump, 120);
      setTimeout(jump, 300);
    });
  }, []);

  const scrollTo = (hash: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (!el) return;
    // The pinned scroll-stories (problem/hub/plugin) collapse their
    // tracks once passed and self-compensate the scroll — a SMOOTH
    // scroll animates straight through that and lands on the wrong
    // section. Jump instantly, then re-align over the next few frames
    // as those sections settle, so we land exactly on the target.
    const jump = () =>
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY,
        behavior: "instant" as ScrollBehavior,
      });
    jump();
    requestAnimationFrame(jump);
    requestAnimationFrame(() => requestAnimationFrame(jump));
    setTimeout(jump, 90);
    setTimeout(jump, 200);
  };

  const goTo = (path: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <>
      {/* Nav */}
      <nav className="nav" id="nav" data-scrolled={scrolled ? "true" : "false"}>
        <div className="wrap nav-inner">
          <a href="#top" onClick={scrollTo("#top")} className="brand">
            <OptImg
              src="/foreman-ui/foreman-logo.png"
              widths={[60, 120]}
              alt="Foreman logo"
              width={30}
              height={30}
              loading="eager"
              fetchPriority="high"
            />
            <span>Foreman</span>
          </a>
          <div className="nav-links">
            <a href="#problem" onClick={scrollTo("#problem")}>
              The problem
            </a>
            <a href="#capabilities" onClick={scrollTo("#capabilities")}>
              What he does
            </a>
            <a href="#pricing" onClick={scrollTo("#pricing")}>
              Pricing
            </a>
            <a href="/articles" onClick={goTo("/articles")}>
              Articles
            </a>
            <a
              href="/get-started"
              onClick={goTo("/get-started")}
              className="nav-cta"
            >
              Apply for a slot
            </a>
          </div>
        </div>
      </nav>

      {/* Hero — orbit composition, Owen 2026-07-31. Full-bleed AI
          podium canvas (public/hero/hero-bg.png) with copy on the
          left and three HTML card replicas (Performance /
          Integrations / Tasks) floating in 3D around the podium.
          Cards are decorative — aria-hidden; the real content is the
          copy column. Canvas <img> is the LCP element. */}
      <section className="hero hero--orbit" id="top" data-hero-bg="podium">
        <div className="orbit-canvas" aria-hidden="true">
          <OptImg
            src="/hero/hero-bg.png"
            widths={[1280, 1536]}
            alt=""
            loading="eager"
            fetchPriority="high"
          />
        </div>

        <div className="hero-inner">
          <div className="orbit-copy">
            <h1 className="hero-h" id="hero-headline">
              <span className="ln">Foreman runs</span>
              <span className="ln">your company's</span>
              <span className="ln">
                <span className="accent">admin.</span>
              </span>
            </h1>

            <div className="cta">
              <a
                href="/get-started"
                onClick={goTo("/get-started")}
                className="btn-go"
              >
                Apply for a founder slot
                <svg
                  className="ar"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
              </a>
              <a
                href="#plugin"
                onClick={scrollTo("#plugin")}
                className="lnk-go"
              >
                See how he works
              </a>
            </div>
          </div>

          {/* Orbit scene — the three cards. Structure per card:
              .ocard-pose  entrance fade + absolute position
              └ .ocard-tilt   static 3D pose (flattens on hover)
                └ .ocard-float  idle bob loop
                  └ .ocard      visual surface */}
          <div className="orbit-scene" aria-hidden="true">
            {/* Mobile-only podium view — same URLs as the desktop
                canvas, so the browser fetches the image once. */}
            <div className="orbit-scene-img">
              <OptImg src="/hero/hero-bg.png" widths={[1280, 1536]} alt="" />
            </div>

            {/* Performance — up-right of the podium */}
            <div className="ocard-pose pose-perf">
              <div className="ocard-tilt">
                <div className="ocard-float">
                  <div className="ocard ocard-perf">
                    <span className="ocard-t">Performance</span>
                    <svg
                      className="perf-chart"
                      viewBox="0 0 264 128"
                      fill="none"
                    >
                      <defs>
                        <linearGradient
                          id="perfFill"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0"
                            stopColor="#b8e34f"
                            stopOpacity="0.30"
                          />
                          <stop
                            offset="1"
                            stopColor="#b8e34f"
                            stopOpacity="0.02"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        className="perf-ghost"
                        d="M10 74 C 34 48, 52 44, 74 58 C 96 72, 112 92, 136 84 C 158 77, 168 58, 190 60 C 210 62, 224 74, 250 66"
                        stroke="#141c10"
                        strokeOpacity="0.07"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <path
                        className="perf-ghost"
                        d="M10 60 C 38 78, 60 88, 86 76 C 110 65, 122 46, 148 50 C 172 54, 186 70, 212 62 C 228 57, 240 48, 252 44"
                        stroke="#141c10"
                        strokeOpacity="0.05"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <path
                        className="perf-area"
                        d="M10 88 C 28 74, 40 58, 58 58 C 76 58, 84 84, 100 88 C 116 92, 128 74, 144 72 C 158 70, 166 82, 180 78 C 200 72, 216 52, 238 26 L 238 112 L 10 112 Z"
                        fill="url(#perfFill)"
                      />
                      <path
                        className="perf-line"
                        d="M10 88 C 28 74, 40 58, 58 58 C 76 58, 84 84, 100 88 C 116 92, 128 74, 144 72 C 158 70, 166 82, 180 78 C 200 72, 216 52, 238 26"
                        stroke="#a6d83c"
                        strokeWidth="5"
                        strokeLinecap="round"
                      />
                      <circle
                        className="perf-halo"
                        cx="238"
                        cy="26"
                        r="13"
                        fill="#b8e34f"
                      />
                      <circle
                        className="perf-dot"
                        cx="238"
                        cy="26"
                        r="7.5"
                        fill="#8fc832"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Integrations — right of the podium */}
            <div className="ocard-pose pose-integr">
              <div className="ocard-tilt">
                <div className="ocard-float">
                  <div className="ocard ocard-integr">
                    <span className="ocard-t">Integrations</span>
                    <div className="integr-row">
                      <span className="tile">
                        <img
                          src="/hero/logo-outlook.jpg"
                          alt=""
                          loading="lazy"
                        />
                      </span>
                      <span className="tile">
                        <img
                          src="/hero/logo-whatsapp-sq.jpg"
                          alt=""
                          loading="lazy"
                        />
                      </span>
                      <span className="tile">
                        <img
                          src="/hero/logo-simpro-mark.png"
                          alt=""
                          loading="lazy"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tasks — down-left of the podium */}
            <div className="ocard-pose pose-tasks">
              <div className="ocard-tilt">
                <div className="ocard-float">
                  <div className="ocard ocard-tasks">
                    <span className="ocard-t">Tasks</span>
                    {heroTasks.map((row, i) => (
                      <div
                        key={i}
                        className={
                          "trow" +
                          (row.done ? " is-done" : "") +
                          (row.entrance ? ` ${row.entrance}` : "")
                        }
                        onClick={() => toggleHeroTask(i)}
                      >
                        <span className="tcheck">
                          <svg viewBox="0 0 16 16" fill="none">
                            <path
                              d="M3.5 8.5l3 3 6-6.5"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span className="tbar"></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem — "fill Foreman up", a pinned scroll story. One
          scene, two pixel-aligned AI images: two desks left, one
          desk right, hollow glass Foreman logo centre. The filled
          variant sits on top, clip-revealed inside the logo region
          (--fill, stepped in thirds). Task bubbles hang over the
          desks with crawling grey bars and live count-up timers —
          no lime in the before-state. Each pour beat sends one
          bubble into the logo and raises the liquid; on .resolved
          tick chips pop back at the desks (seconds, not minutes)
          and the narration swaps to the payoff line. Colour
          arrives with him. */}
      <section className="section problem vibe scene-problem" id="problem">
        <div className="scene-sticky">
        <div className="scene-canvas" aria-hidden="true">
          <div className="scene-zoom">
            <OptImg
              src="/hero/problem-stage.png"
              widths={[1280, 1536]}
              alt=""
            />
            {["problem-stage-1", "problem-stage-2", "problem-stage-full"].map(
              (name, i) => (
                <div className={`scene-stage st-${i + 1}`} key={name}>
                  <OptImg
                    src={`/hero/${name}.png`}
                    widths={[1280, 1536]}
                    alt=""
                  />
                </div>
              ),
            )}
          </div>
        </div>
        <div className="scene-chips" aria-hidden="true">
          {OFFICE_TASKS.map((t, i) => (
            <div className={`task-pose tp${i + 1}`} key={t.label}>
              <div className="task-fly">
                <div className="task-float">
                  <div className="task-bubble">
                    <span className="task-t">{t.label}</span>
                    <span className="task-row">
                      <span className="task-bar">
                        <span
                          className="task-fill"
                          style={{ width: `${t.fill}%` }}
                        ></span>
                      </span>
                      <span className="task-timer">
                        {fmtTimer(t.base + officeTick)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {OFFICE_TASKS.map((t, i) => (
            <div className={`desk-done dd${i + 1}`} key={t.label}>
              <span className="fdone-check">
                <svg viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3.5 8.5l3 3 6-6.5"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="desk-done-s">{t.secs}</span>
            </div>
          ))}
        </div>
        <div className="problem-side">
          <div className="eyebrow">The problem</div>
          <div className="pcopy">
            <div className="pcopy-before">
              <h2>Your best people, paid to click.</h2>
              <p>
                Jobs raised by hand. Invoices chased by hand. Notes retyped by
                hand. All day, every day.
              </p>
            </div>
            <div className="pcopy-after" aria-hidden="true">
              <h2>Hours of clicking, done in seconds.</h2>
              <p>
                Foreman takes the grind — your people get back to work that
                matters.
              </p>
            </div>
          </div>
        </div>
        </div>
        {/* Mobile-only static retelling — the pinned stage can't tell
            its story without scroll, so phones get this instead (Owen).
            Desktop hides .flat-story; mobile hides the stage above. */}
        <div className="flat-story sp-flat" aria-hidden="true">
          <div className="eyebrow">The problem</div>
          <h2>Your best people, paid to click.</h2>
          <p className="fs-sub">
            Jobs raised by hand. Invoices chased by hand. Notes retyped by hand.
          </p>
          <div className="fs-tasks">
            {OFFICE_TASKS.map((t) => (
              <div className="fs-task" key={t.label}>
                <span className="fs-t">{t.label}</span>
                <span className="fs-times">
                  <span className="fs-hand">{t.hand} by hand</span>
                  <span className="fs-arrow">→</span>
                  <b className="fs-fm">{t.fm}</b>
                </span>
              </div>
            ))}
          </div>
          <p className="fs-punch">
            Hours off your team&apos;s week, <em>handed to Foreman.</em>
          </p>
        </div>
      </section>

      {/* Meet Foreman — the hub scene (v3: pinned + full-frame).
          Render pair hub-scene / hub-scene-lit: four glass brand
          logos around the hard-hat figure. The 3:2 frame always
          shows the WHOLE render (largest-fit box, side margins in
          the render's own edge colour); all overlays live inside
          the frame in content-%, and the glow windows are static
          clip-path %s. Scroll q drives the beats — command, then
          each system glowing in work order, then the closing line
          as Teams glows (it wasn't in the command: the "anything
          you can ask" wink). One-way .done latch like the Problem
          scene. Keep the useEffect + preview script in sync. */}
      <section className="section vibe scene-hub" id="meet">
        <div className="scene-sticky">
          <div className="hub-frame">
            <OptImg src="/hero/hub-scene.png" widths={[1280, 1536]} alt="" />
            {[1, 2, 3, 4].map((n) => (
              <div className={`hub-lit hl-${n}`} key={n} aria-hidden="true">
                <OptImg
                  src="/hero/hub-scene-lit.png"
                  widths={[1280, 1536]}
                  alt=""
                />
              </div>
            ))}
            <div className="hub-cmd" aria-hidden="true">
              <div className="hub-cmd-h">
                <span className="fdock-dot"></span>
                you, to Foreman
              </div>
              <p>
                Audit yesterday&apos;s job notes in{" "}
                <span className="cb-pair">
                  <img src="/hero/logo-simpro-mark.png" alt="" />
                  <b className="cb-sp">SimPro</b>
                </span>{" "}
                and{" "}
                <span className="cb-pair">
                  <img src="/hero/logo-whatsapp-sq.jpg" alt="" />
                  <b className="cb-wa">WhatsApp</b>
                </span>{" "}
                the techs who left fields blank. Send me a report in{" "}
                <span className="cb-pair">
                  <img src="/hero/logo-outlook.jpg" alt="" />
                  <b className="cb-ol">Outlook</b>
                </span>
                .
              </p>
            </div>
            <span className="hub-note hn-1" aria-hidden="true">
              3 jobs found in SimPro
            </span>
            <span className="hub-note hn-2" aria-hidden="true">
              2 techs messaged on WhatsApp
            </span>
            <span className="hub-note hn-3" aria-hidden="true">
              report sent through Outlook
            </span>
            <p className="hub-fin" aria-hidden="true">
              All from one sentence.{" "}
              <em>His limits are your imagination.</em>
            </p>
          </div>
          <div className="hub-copy">
            <div className="eyebrow">Meet Foreman</div>
            <h2>Your systems don&apos;t talk to each other. He does.</h2>
          </div>
        </div>
        {/* Mobile-only static retelling: one message in → three systems
            handled. Desktop uses the pinned lighting sequence above. */}
        <div className="flat-story hub-flat" aria-hidden="true">
          <div className="eyebrow">Meet Foreman</div>
          <h2>Your systems don&apos;t talk to each other. He does.</h2>
          <div className="fs-cmd">
            <div className="hub-cmd-h">
              <span className="fdock-dot"></span>
              you, to Foreman
            </div>
            <p>
              Audit yesterday&apos;s job notes in{" "}
              <span className="cb-pair">
                <img src="/hero/logo-simpro-mark.png" alt="" />
                <b className="cb-sp">SimPro</b>
              </span>{" "}
              and{" "}
              <span className="cb-pair">
                <img src="/hero/logo-whatsapp-sq.jpg" alt="" />
                <b className="cb-wa">WhatsApp</b>
              </span>{" "}
              the techs who left fields blank. Send me a report in{" "}
              <span className="cb-pair">
                <img src="/hero/logo-outlook.jpg" alt="" />
                <b className="cb-ol">Outlook</b>
              </span>
              .
            </p>
          </div>
          <div className="fs-results">
            <div className="fs-result">
              <span className="fs-check"></span>3 jobs found in SimPro
            </div>
            <div className="fs-result">
              <span className="fs-check"></span>2 techs messaged on WhatsApp
            </div>
            <div className="fs-result">
              <span className="fs-check"></span>report sent through Outlook
            </div>
          </div>
          <p className="fs-punch">
            All from one sentence.{" "}
            <em>His limits are your imagination.</em>
          </p>
        </div>
      </section>

      {/* Capabilities → "Three ways to put him to work" (glass
          panels, typographic — deliberately no pin after two pinned
          stories). Owen's taxonomy: you ask him (any channel) / he
          wakes on events / he runs on the clock. Command cards
          reuse the hub grammar — more sentences you could say. The
          HTML is the layout reference for an AI glass-panel render
          (text stays HTML for legibility); until that lands this
          CSS glass IS the shipping look. */}
      <section className="section vibe caps-glass" id="capabilities">
        <div className="wrap">
          <div className="caps-head">
            <div className="eyebrow">How you use him</div>
            <h2 style={{ marginTop: 20 }}>
              Three ways to put him to work.
            </h2>
            <p>
              Chat with him like an employee, or set him up once and let
              events and the clock wake him.
            </p>
          </div>

          {/* Three EXAMPLES of what you can say (not a conversation):
              type badge, glass bubble, one plain fact line stating
              what happens. img alts carry the bubble text for
              SEO/screen readers. */}
          <div className="ask-examples">
            <div className="ask-ex">
              <OptImg
                src="/hero/ask-bubble-1.png"
                widths={[640, 1120]}
                alt="“Make an invoice for job 12.”"
              />
              <p className="ask-fact">Done here and now.</p>
            </div>

            <div className="ask-ex">
              <OptImg
                src="/hero/ask-bubble-2.png"
                widths={[640, 1120]}
                alt="“Whenever we complete a job, make sure the tech's notes are good. If not, text the tech.”"
              />
              <p className="ask-fact">Runs whenever a job completes.</p>
            </div>

            <div className="ask-ex">
              <OptImg
                src="/hero/ask-bubble-3.png"
                widths={[640, 1120]}
                alt="“Every Friday at 8am, phone me about the unpaid invoices.”"
              />
              <p className="ask-fact">Runs every Friday at 8am. Religiously.</p>
            </div>
          </div>

          <div className="job-apply">
            <a
              href="/get-started"
              onClick={goTo("/get-started")}
              className="btn btn-primary"
            >
              Apply for a founder slot
            </a>
            <p className="job-apply-meta">14 days free</p>
          </div>
        </div>
      </section>

      {/* How he plugs in — "Out to prove himself" (v3: four-scene
          pinned scroll story). Four renders (prove-1..4, subjects
          right-of-centre) dissolve as scroll steps through his
          first two weeks; the lime tag + caption crossfade per
          beat top-left. Same one-way .done latch as Problem/Hub.
          Keep the useEffect + preview script in sync. */}
      <section className="section vibe plugin" id="plugin">
        <div className="scene-sticky">
          <div className="prove-frame" aria-hidden="true">
            {[1, 2, 3, 4].map((n) => (
              <div className={`prove-img pi-${n}`} key={n} aria-hidden="true">
                <OptImg
                  src={`/hero/prove-${n}.png`}
                  widths={[1280, 1536]}
                  alt=""
                />
              </div>
            ))}
          </div>
          <div className="prove-copy">
            <div className="eyebrow">His first two weeks</div>
            <h2>Out to prove himself.</h2>
            <div className="prove-cap">
              <div className="pc pc-1">
                <p>Within minutes, Foreman understands how your company specifically uses SimPro, the lingo and the terms you use. And he keeps learning over time.</p>
              </div>
              <div className="pc pc-2">
                <p>Within days, your entire company is pushing him to his limits, seeing what he&apos;s capable of, delegating tasks that used to take hours.</p>
              </div>
              <div className="pc pc-3">
                <p>Within a week, he isn&apos;t just answering messages. He&apos;s working round the clock on anything you tell him, keeping your company healthy by the rules you teach him.</p>
              </div>
              <div className="pc pc-4">
                <p>By two weeks, he knows your business and your people inside out. Everyone leans on him daily. Now you decide: hire him or fire him, and what work would look like without him.</p>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile-only static retelling: the two-week arc as a vertical
            timeline (the desktop crossfade can't be read without scroll). */}
        <div className="flat-story pv-flat" aria-hidden="true">
          <div className="eyebrow">His first two weeks</div>
          <h2>Out to prove himself.</h2>
          <ol className="fs-timeline">
            <li className="fs-step">
              <span className="fs-when">Minutes</span>
              <p>He learns how your company specifically uses SimPro — your lingo, your terms — and keeps learning over time.</p>
            </li>
            <li className="fs-step">
              <span className="fs-when">Days</span>
              <p>Your entire company is pushing him to his limits, delegating tasks that used to take hours.</p>
            </li>
            <li className="fs-step">
              <span className="fs-when">A week</span>
              <p>He&apos;s working round the clock on anything you tell him, keeping your company healthy by the rules you teach him.</p>
            </li>
            <li className="fs-step">
              <span className="fs-when">Two weeks</span>
              <p>He knows your business and people inside out. Now you decide: hire him or fire him — and what work would look like without him.</p>
            </li>
          </ol>
        </div>
      </section>

      {/* Security — "He can / He never". Trust in a person, not
          infrastructure: the hard-hat figure holds a big glass key
          (security-key.png, bgAlpha'd) between the two lists —
          what he's allowed and what he's forbidden. Rows stagger
          in per side on one-way .in-view. Keep the useEffect +
          preview script in sync. */}
      <section className="section vibe security-glass" id="security">
        <div className="wrap">
          <div className="sec-head">
            <div className="eyebrow">Built secure, from the ground</div>
            <h2 style={{ marginTop: 20 }}>
              The kind of employee you can give the keys to.
            </h2>
          </div>
          <div className="sec-cols">
            <div className="sec-list sec-can">
              <h3>He can</h3>
              <div className="sec-row">
                <span className="sec-mark">
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3.5 8.5l3 3 6-6.5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <p>Work the systems you gave him. SimPro, your inbox, Teams, WhatsApp.</p>
              </div>
              <div className="sec-row">
                <span className="sec-mark">
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3.5 8.5l3 3 6-6.5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <p>Message your team, verified staff on verified numbers.</p>
              </div>
              <div className="sec-row">
                <span className="sec-mark">
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M3.5 8.5l3 3 6-6.5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <p>Show you everything he has done. Every action, in a log you can read any time.</p>
              </div>
            </div>
            <div className="sec-list sec-never">
              <h3>He never</h3>
              <div className="sec-row">
                <span className="sec-mark">
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4.5 4.5l7 7M11.5 4.5l-7 7"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <p>Trains AI on your data, or shares it with another business. Yours stays yours.</p>
              </div>
              <div className="sec-row">
                <span className="sec-mark">
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4.5 4.5l7 7M11.5 4.5l-7 7"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <p>Breaks the limits you set. He can only do what you allow, nothing more.</p>
              </div>
              <div className="sec-row">
                <span className="sec-mark">
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4.5 4.5l7 7M11.5 4.5l-7 7"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <p>Emails or texts your customers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder cohort — pricing as an EMPLOYMENT OFFER letter,
          modelled on the real UK written-statement layout so it
          reads instantly as a job document. Trial terms are the
          particulars; the CTA is the signature. One-way .in-view
          via IO. Keep the useEffect + preview in sync. */}
      <section className="section vibe pricing-offer" id="pricing">
        <div className="wrap">
          <div className="offer-head">
            <div className="eyebrow">Founder cohort</div>
            <h2 style={{ marginTop: 20 }}>Batch two is open.</h2>
            <p>
              First batch is in. Hire him for fourteen days free. Keep him if
              he earns it.
            </p>
          </div>
          <div className="offer-card">
            <span className="offer-stamp">Batch 2 · Open</span>
            <div className="offer-h">Employment Offer</div>
            <div className="offer-subh">Written statement of particulars</div>
            <div className="offer-rows">
              <div className="offer-row">
                <span className="offer-k">Position</span>
                <span className="offer-v">Foreman, AI employee</span>
              </div>
              <div className="offer-row">
                <span className="offer-k">Reports to</span>
                <span className="offer-v">You</span>
              </div>
              <div className="offer-row">
                <span className="offer-k">Start date</span>
                <span className="offer-v">Today</span>
              </div>
              <div className="offer-row">
                <span className="offer-k">Salary</span>
                <span className="offer-v">&pound;0 for the first 14 days</span>
              </div>
              <div className="offer-row">
                <span className="offer-k">Hours</span>
                <span className="offer-v">
                  Round the clock. He doesn&apos;t do holidays.
                </span>
              </div>
              <div className="offer-row">
                <span className="offer-k">Place of work</span>
                <span className="offer-v">
                  SimPro, Teams, WhatsApp, email, phone
                </span>
              </div>
              <div className="offer-row">
                <span className="offer-k">Notice period</span>
                <span className="offer-v">
                  None. Fire him on the spot, free.
                </span>
              </div>
            </div>
            <div className="offer-sign">
              <a
                href="/get-started"
                onClick={goTo("/get-started")}
                className="offer-btn"
              >
                Apply for a founder slot →
              </a>
              <div className="offer-meta">14 days free · no card</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — centred type over the bgAlpha podium render;
          lime signature button matches the offer letter. */}
      <section className="section vibe final-glass" id="cta">
        <div className="wrap">
          <div className="fg-wrap">
            <div className="eyebrow">Ready when you are</div>
            <h2>
              Stop hiring admins to patch the chaos.
              <br />
              Hire Foreman.
            </h2>
            <p className="final-sub">
              Five minutes to connect. Fourteen days free. Fire him at any
              time.
            </p>
            <div className="final-ctas">
              <a
                href="/get-started"
                onClick={goTo("/get-started")}
                className="offer-btn"
              >
                Apply for a founder slot →
              </a>
              <a
                href="#capabilities"
                onClick={scrollTo("#capabilities")}
                className="final-ghost"
              >
                See what he runs →
              </a>
            </div>
          </div>
          <div className="final-podium" aria-hidden="true">
            <OptImg src="/hero/cta-podium.png" widths={[640, 1120]} alt="" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <OptImg
              src="/foreman-ui/foreman-logo.png"
              widths={[60, 120]}
              alt="Foreman"
            />
            <span>Foreman</span>
          </div>
          <div className="footer-links">
            <a href="/privacy" onClick={goTo("/privacy")}>
              Privacy
            </a>
            <a href="#">Security</a>
            <a href="#">Contact</a>
            <a href="/articles" onClick={goTo("/articles")}>
              Articles
            </a>
            <a
              href="/articles/which-foreman-is-which"
              onClick={goTo("/articles/which-foreman-is-which")}
            >
              Which Foreman?
            </a>
          </div>
          <div className="footer-meta">© 2026 · Built for the trades</div>
        </div>
      </footer>
    </>
  );
}
